'use client';

import React from 'react';

import Link from 'next/link';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AuthLoadError } from '@/components/AuthLoadError';
import { ThreadFeedItem } from '@/components/FeedItem';
import { useUserInfo } from '@/context/UserInfoContext';
import { useFeedSection, type FeedSectionState } from '@/hooks/useFeedSection';
import { useFeedUrlState } from '@/hooks/useFeedUrlState';
import type { DisplayUser } from '@/lib/api';

import type { HomeFeedCountParam } from './homeFeedQuery';
import { HOME_FEED_COUNT_PARAMS, HOME_FEED_INCREMENT, MAX_HOME_FEED_COUNT } from './homeFeedQuery';

type AuthenticatedFeedSection = 'personal' | 'social';

/** Renders the shared feed-card loading placeholders. */
const FeedSkeletons: React.FC = () => (
  <Stack spacing={2}>
    {[...Array(5)].map((_, index) => (
      <Box key={index}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 1 }} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </Box>
    ))}
  </Stack>
);

type PublicHomeFeedProps = {
  numItems: number;
  replaceFeedCount: (nextCount: number, countParam: HomeFeedCountParam) => void;
};

/** Preserves the anonymous public-feed request, pagination, and polling behavior. */
const PublicHomeFeed: React.FC<PublicHomeFeedProps> = ({ numItems, replaceFeedCount }) => {
  const section = useFeedSection('public', numItems, { suppressItemsOnError: false });

  const handleLoadMore = (): void => {
    if (section.isLoadingMore) return;

    const next = Math.min(numItems + HOME_FEED_INCREMENT, MAX_HOME_FEED_COUNT);
    section.beginLoadMore(next);
    replaceFeedCount(next, 'count');
  };

  return (
    <Box component="section" sx={{ width: '100%', mb: 3 }} aria-labelledby="public-feed">
      <Typography id="public-feed" component="h2" variant="h5" sx={{ mb: 2 }}>
        Product Reviews and Discussion
      </Typography>
      {section.visibleFeedItems && section.visibleFeedItems.length > 0 ? (
        <Stack spacing={2}>
          {section.visibleFeedItems.map((item) => (
            <ThreadFeedItem key={item.thread.target_public_doc_id} item={item} showTopic={true} />
          ))}
        </Stack>
      ) : !section.hasResolvedCurrentRequest ? (
        <FeedSkeletons />
      ) : section.error ? (
        <Typography>Error loading content. Please try again later.</Typography>
      ) : (
        <Typography>
          Nothing to see!{' '}
          <MuiLink component={Link} href="/login">
            Log in
          </MuiLink>{' '}
          to see personalized content.
        </Typography>
      )}
      {section.error && section.visibleFeedItems && section.visibleFeedItems.length > 0 ? (
        <Typography>Error loading content. Please try again later.</Typography>
      ) : null}
      {(section.hasResolvedCurrentRequest || section.isLoadingMore) &&
        !section.error &&
        numItems < MAX_HOME_FEED_COUNT && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              onClick={handleLoadMore}
              aria-label="Load more content"
              disabled={section.isLoadingMore}
              variant="outlined"
              size="medium"
            >
              {section.isLoadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
    </Box>
  );
};

type AuthenticatedSectionProps = {
  heading: string;
  headingHref?: string;
  headingId: string;
  emptyCopy: string;
  errorCopy: string;
  numItems: number;
  onLoadMore: () => void;
  /** Controls whether participant identities render inline or at the card's bottom right. */
  participantPlacement: 'inline' | 'bottom-right';
  section: FeedSectionState;
  userInfo: DisplayUser;
};

/** Renders one independently resolved authenticated feed section. */
const AuthenticatedSection: React.FC<AuthenticatedSectionProps> = ({
  heading,
  headingHref,
  headingId,
  emptyCopy,
  errorCopy,
  numItems,
  onLoadMore,
  participantPlacement,
  section,
  userInfo,
}) => {
  const loadMoreLabel = `Load more ${heading}`;

  return (
    <Box component="section" aria-labelledby={headingId} sx={{ width: '100%' }}>
      <Typography id={headingId} component="h2" variant="h5" sx={{ mb: 2 }}>
        {headingHref ? (
          <MuiLink component={Link} href={headingHref} color="inherit" underline="hover">
            {heading}
          </MuiLink>
        ) : (
          heading
        )}
      </Typography>
      {section.error ? (
        <Typography>{errorCopy}</Typography>
      ) : section.visibleFeedItems && section.visibleFeedItems.length > 0 ? (
        <Stack spacing={2}>
          {section.visibleFeedItems.map((item) => (
            <ThreadFeedItem
              key={item.thread.target_public_doc_id}
              item={item}
              showTopic={true}
              currentUser={userInfo}
              participantPlacement={participantPlacement}
            />
          ))}
        </Stack>
      ) : !section.hasResolvedCurrentRequest ? (
        <FeedSkeletons />
      ) : (
        <Typography>{emptyCopy}</Typography>
      )}
      {(section.hasResolvedCurrentRequest || section.isLoadingMore) &&
        !section.error &&
        numItems < MAX_HOME_FEED_COUNT && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              onClick={onLoadMore}
              aria-label={loadMoreLabel}
              disabled={section.isLoadingMore}
              variant="outlined"
              size="medium"
            >
              {section.isLoadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
    </Box>
  );
};

type JourneyFeedSectionProps = {
  countParam: HomeFeedCountParam;
  emptyCopy: string;
  errorCopy: string;
  heading: string;
  headingHref?: string;
  headingId: string;
  numItems: number;
  replaceFeedCount: (nextCount: number, countParam: HomeFeedCountParam) => void;
  section: AuthenticatedFeedSection;
  userInfo: DisplayUser;
};

/** Owns one authenticated journey section, including its independent pagination URL. */
export const JourneyFeedSection: React.FC<JourneyFeedSectionProps> = ({
  countParam,
  emptyCopy,
  errorCopy,
  heading,
  headingHref,
  headingId,
  numItems,
  replaceFeedCount,
  section,
  userInfo,
}) => {
  const feedSection = useFeedSection(section, numItems, { suppressItemsOnError: true });

  const handleLoadMore = (): void => {
    if (feedSection.isLoadingMore) return;

    const next = Math.min(numItems + HOME_FEED_INCREMENT, MAX_HOME_FEED_COUNT);
    feedSection.beginLoadMore(next);
    replaceFeedCount(next, countParam);
  };

  return (
    <AuthenticatedSection
      heading={heading}
      headingHref={headingHref}
      headingId={headingId}
      emptyCopy={emptyCopy}
      errorCopy={errorCopy}
      numItems={numItems}
      onLoadMore={handleLoadMore}
      participantPlacement={section === 'social' ? 'bottom-right' : 'inline'}
      section={feedSection}
      userInfo={userInfo}
    />
  );
};

type AuthenticatedHomeFeedProps = {
  personalNumItems: number;
  replaceFeedCount: (nextCount: number, countParam: HomeFeedCountParam) => void;
  socialNumItems: number;
  userInfo: DisplayUser;
};

/** Coordinates independently paginated Personal and Social feed sections. */
const AuthenticatedHomeFeed: React.FC<AuthenticatedHomeFeedProps> = ({
  personalNumItems,
  replaceFeedCount,
  socialNumItems,
  userInfo,
}) => {
  return (
    <Stack spacing={4} sx={{ width: '100%', mb: 3 }}>
      <JourneyFeedSection
        countParam="social_count"
        heading="Friends' Journeys"
        headingHref="/journeys/friends/"
        headingId="social-feed"
        emptyCopy="Nothing in your social feed yet."
        errorCopy="Error loading your social feed. Please try again later."
        numItems={socialNumItems}
        replaceFeedCount={replaceFeedCount}
        section="social"
        userInfo={userInfo}
      />
      <JourneyFeedSection
        countParam="personal_count"
        heading="Your Journeys"
        headingHref="/journeys/own/"
        headingId="personal-feed"
        emptyCopy="Nothing in your personal feed yet."
        errorCopy="Error loading your personal feed. Please try again later."
        numItems={personalNumItems}
        replaceFeedCount={replaceFeedCount}
        section="personal"
        userInfo={userInfo}
      />
    </Stack>
  );
};

type PendingAuthHomeFeedProps = {
  hasError: boolean;
  reloadUserInfo: () => Promise<void>;
};

/** Keeps signed-out content hidden until the authentication probe resolves. */
const PendingAuthHomeFeed: React.FC<PendingAuthHomeFeedProps> = ({ hasError, reloadUserInfo }) => (
  <Box component="section" sx={{ width: '100%', mb: 3 }} aria-labelledby="pending-feed">
    <Typography id="pending-feed" component="h2" variant="h5" sx={{ mb: 2 }}>
      Feed
    </Typography>
    {hasError ? <AuthLoadError onRetry={reloadUserInfo} /> : <FeedSkeletons />}
  </Box>
);

/** Selects the public or authenticated feed tree after normalizing its URL counts. */
const HomeFeed: React.FC = () => {
  const { counts, replaceFeedCount } = useFeedUrlState(HOME_FEED_COUNT_PARAMS);
  const { userInfoState, userInfo, reloadUserInfo } = useUserInfo();
  const publicNumItems = counts.count!;
  const personalNumItems = counts.personal_count!;
  const socialNumItems = counts.social_count!;

  if (userInfo === null && userInfoState.status !== 'signed_out') {
    return (
      <PendingAuthHomeFeed
        hasError={userInfoState.status === 'error'}
        reloadUserInfo={reloadUserInfo}
      />
    );
  }

  return userInfo === null ? (
    <PublicHomeFeed numItems={publicNumItems} replaceFeedCount={replaceFeedCount} />
  ) : (
    <AuthenticatedHomeFeed
      personalNumItems={personalNumItems}
      replaceFeedCount={replaceFeedCount}
      socialNumItems={socialNumItems}
      userInfo={userInfo}
    />
  );
};

export default HomeFeed;
